// Copyright (c) Zammo Inc.

import React, { useState, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import Modal from 'react-modal';

import SharedInput from '../inputs/Input';
import Button from '../buttons/Button';
import Select from '../Select';
import { palette } from '../../styles/variables';
import URLTooltip from '../Tooltip/URLTooltip';
import { forceToAppendHttps } from '../../utils/helpers';

const Center = styled.div`
  text-align: center;
  line-height: 1.5;
`;

const Title = styled.div`
  margin: 1rem 0;
  span {
    color: ${palette.red};
  }
`;

const Input = styled(SharedInput)`
  text-align: center;
  &::-webkit-input-placeholder {
    text-align: center;
  }

  &:-moz-placeholder {
    text-align: center;
  }
`;

const URLTooltipContainer = styled.div`
  display: inline-flex;
  align-items: center;
  margin-right: 10px;
`;

const FlexCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const style = {
  content: {
    bottom: 'auto',
    height: 'auto', // set height
    left: '50%',
    padding: '2rem',
    right: 'auto',
    top: '50%', // start from center
    transform: 'translate(-50%, -50%)', // adjust top "up" based on height
    width: '40%',
    maxWidth: '40rem',
  },
};

const experiences = [
  {
    value: 'webchat',
    label: 'Website Chatbot',
  },
  {
    value: 'voice',
    label: 'Voice',
  },
  {
    value: 'tts-avatar',
    label: 'TTS Avatar (Preview)',
  },
];

interface Props {
  isOpen: boolean;
  onDismiss: () => void;
}
const TokenModal = ({ isOpen, onDismiss }: Props) => {
  const [token, setToken] = useState('');
  const [experience, setExperience] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const history = useHistory();

  const handleConfirm = () => {
    const query = new URLSearchParams();
    if (token) {
      query.append('token', token);
    }

    if (experience) {
      query.append('channel', experience);
    }

    if (websiteUrl) {
      query.append('url', forceToAppendHttps(websiteUrl));
    }

    history.push(`/?${query.toString()}`);
    onDismiss();
  };

  const isConfirmDisabled = useMemo(() => {
    if (!token || !experience) {
      return true;
    }
    return false;
  }, [token, experience]);

  const handleExperienceClick = useCallback(
    (a) => {
      setExperience(a.value);
    },
    [setExperience]
  );

  return (
    <Modal isOpen={isOpen} style={style}>
      <Center>
        <div>Welcome to the Zammo simulator.</div>
        <div style={{ marginBottom: '2rem' }}>Please enter your simulator token</div>
        <Title>
          Experience: <span>*</span>
        </Title>
        <Select placeholder="" options={experiences} onClick={handleExperienceClick} selectedValue={experience} />
        <Title>
          Token: <span>*</span>
        </Title>
        <Input type="text" value={token} onChange={(e) => setToken(e.target.value)} />
        {experience === 'webchat' && (
          <>
            <Title>
              <FlexCenter>
                <URLTooltipContainer>
                  <URLTooltip tooltipPosition="left" />
                </URLTooltipContainer>
                Website URL:
              </FlexCenter>
            </Title>
            <Input
              type="text"
              placeholder="https://"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
            />
          </>
        )}
        <Button type="button" style={{ marginTop: '2rem' }} disabled={isConfirmDisabled} onClick={handleConfirm}>
          Confirm
        </Button>
      </Center>
    </Modal>
  );
};

export default TokenModal;
